import { Injectable, inject } from '@angular/core';
import * as L from 'leaflet';
import * as Turf from '@turf/turf';
import { Router } from '@angular/router';
import { AirspaceElement, MapLayerTypeEnum, WebrqaGeoJSONOptions } from '../../model/map.model';
import { StyleType } from '../../model/layers.model';
import { MapLayerService } from './map-layer.service';

@Injectable({
  providedIn: 'root'
})
export class RqaLayerCreatorService {
  private mapLayerService = inject(MapLayerService);
  private router = inject(Router);

  createLayers(layers: AirspaceElement[], map: L.Map): { [key: string]: L.LayerGroup } {
    const groupedLayers: { [key: string]: L.LayerGroup } = {};
    this.mapLayerService.clearAllLayers(map);

    layers.forEach((layerData) => {
      const { designator, type, geometry, styles, availabilities, info } = layerData;
      if (!groupedLayers[type]) {
        groupedLayers[type] = L.layerGroup().addTo(map);
      }

      const layer = new L.GeoJSON(
        geometry as Turf.GeometryObject,
        {
          designator,
          type,
          styles,
          availabilities,
          info,
          className: designator
        } as WebrqaGeoJSONOptions
      );

      if (layer) {
        groupedLayers[type].addLayer(layer);
        this.mapLayerService.setLayerStyles(layer, styles, StyleType.ACTIVATED);
      }
    });

    return groupedLayers;
  }

  setupFilterLayerTypes(groupedLayers: { [key: string]: L.LayerGroup }): void {
    const currentFilterTypes = this.mapLayerService.filterLayerTypes();
    let updatedFilterTypes = [...currentFilterTypes];

    if (updatedFilterTypes.length === 0) {
      updatedFilterTypes = Object.keys(groupedLayers).map((type) => {
        const dynamicLayerTypes: string[] = [MapLayerTypeEnum.RQA, MapLayerTypeEnum.ORDER, MapLayerTypeEnum.TEMPLATE];
        return {
          type: type,
          visible: dynamicLayerTypes.includes(type),
          checked: dynamicLayerTypes.includes(type)
        };
      });
    } else {
      Object.keys(groupedLayers).forEach((type) => {
        const isExist = updatedFilterTypes.find((el) => el.type === type);
        if (!isExist) {
          const dynamicLayerTypes: string[] = [MapLayerTypeEnum.RQA, MapLayerTypeEnum.ORDER, MapLayerTypeEnum.TEMPLATE];
          updatedFilterTypes.push({
            type: type,
            visible: dynamicLayerTypes.includes(type),
            checked: dynamicLayerTypes.includes(type)
          });
        }
      });
    }

    this.mapLayerService.setFilterLayerTypes(updatedFilterTypes);
  }

  applyLayerVisibility(map: L.Map): void {
    const filterTypes = this.mapLayerService.filterLayerTypes();
    const disabledAreas = this.mapLayerService.disabledAreas();

    filterTypes.forEach((el) => {
      let checked = el.checked;
      if (this.router.url === '/') {
        checked = disabledAreas.includes(el.type) ? false : true;
      }
      this.mapLayerService.toggleLayerGroupVisibility(checked, el.type, map);
    });
  }

  setupLayerClickHandlers(
    groupedLayers: { [key: string]: L.LayerGroup },
    onLayerClickCallback: (layer: L.GeoJSON, selectedLayers: AirspaceElement[]) => void
  ): void {
    Object.values(groupedLayers).forEach((layerGroup) => {
      layerGroup.eachLayer((layer) => {
        if (layer instanceof L.GeoJSON) {
          layer.on('click', () => {
            const selectedLayers = [layer.options as AirspaceElement];
            onLayerClickCallback(layer, selectedLayers);
          });
        }
      });
    });
  }
}
